import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Loader2, AlertCircle, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import { useCart } from '../context/CartContext';
import { log } from '../lib/logger';

/**
 * Deep-link landing page used by the Monthly Reorder SMS.
 * URL: `/reorder/{reference}`
 *
 * Flow:
 *  1. Gated route — `RequireCustomer` in App.js redirects to /login if not signed in.
 *  2. Fetch the order from `/orders/me` (we re-use the existing list endpoint to avoid
 *     a new server endpoint and to inherit the customer-ownership check for free).
 *  3. Add every item to the cart, suppress the cart drawer, navigate to /checkout.
 *  4. On error (order doesn't belong to user / not found), show a friendly fallback.
 */
const ReorderPage = () => {
  const { reference } = useParams();
  const navigate = useNavigate();
  const { addItem, setIsOpen } = useCart();
  const [status, setStatus] = useState('loading'); // loading | not_found | error
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    api.get('/orders/me')
      .then((res) => {
        const order = (res.data || []).find((o) => o.reference === reference);
        if (!order) { setStatus('not_found'); return; }
        if (!order.items?.length) { setStatus('not_found'); return; }
        order.items.forEach((it) => {
          addItem({ id: it.product_id, name: it.name, price: it.price, image: it.image || '' }, it.qty);
        });
        setIsOpen(false);
        toast.success(`${order.items.length} item${order.items.length === 1 ? '' : 's'} added to your cart`);
        navigate('/checkout', { replace: true });
      })
      .catch((err) => {
        log.warn('Reorder deep-link failed', err);
        setStatus('error');
      });
  }, [reference, addItem, setIsOpen, navigate]);

  if (status === 'loading') {
    return (
      <main className="max-w-md mx-auto px-6 py-24 text-center" data-testid="reorder-loading">
        <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
          <Loader2 size={26} className="animate-spin text-red-600" />
        </div>
        <h1 className="mt-5 text-2xl font-bold text-[#0a0a0a]">Reordering…</h1>
        <p className="mt-2 text-sm text-slate-500">Re-adding your items from <span className="font-mono">{reference}</span> to your cart.</p>
      </main>
    );
  }

  return (
    <main className="max-w-md mx-auto px-6 py-24 text-center" data-testid="reorder-error">
      <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto">
        <AlertCircle size={26} className="text-amber-600" />
      </div>
      <h1 className="mt-5 text-2xl font-bold text-[#0a0a0a]">
        {status === 'not_found' ? "We couldn't find that order" : 'Something went wrong'}
      </h1>
      <p className="mt-2 text-sm text-slate-500">
        {status === 'not_found'
          ? <>The order <span className="font-mono">{reference}</span> isn't linked to your account, or has no items to reorder.</>
          : 'Please refresh and try again, or browse the shop.'}
      </p>
      <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
        <Link to="/account" className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-slate-200 hover:border-red-300 text-sm font-semibold text-[#0a0a0a] rounded-lg" data-testid="reorder-account-btn">
          <RotateCcw size={13} /> My orders
        </Link>
        <Link to="/" className="inline-block px-4 py-2 bg-red-500 hover:bg-red-400 text-white text-sm font-semibold rounded-lg" data-testid="reorder-shop-btn">
          Browse shop
        </Link>
      </div>
    </main>
  );
};

export default ReorderPage;
